// #ux-redesign-start: Account — Profile + Security + Preferences (Patients)
//
// Tab Profile: UN solo SectionCard que contiene:
//    1. ProfileForm (info personal + tipo de seguro) — sin botones internos.
//    2. Lista de direcciones en tarjetas compactas (modal create/edit).
//    3. Botones Guardar / Cancelar al final del card, conectados al form via
//       el atributo HTML5 `form="account-profile-form"`.
//
// Mobile-first: el SectionCard ocupa el ancho total, las tarjetas de dirección
// pasan a 1 columna en xs y los botones se apilan verticalmente full-width.
// El tab Tabs DS en vertical desktop colapsa horizontalmente bajo 992px.
//
// Security / Preferences: paneles secundarios (sin opción de "eliminar cuenta").
// Sin tab "Legal".
//
// Backwards compat: /admin/profile → ?tab=profile, /admin/settings → preferences.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslate } from 'react-translate';
import moment from 'moment';
import { FormGroup, Input } from 'reactstrap';

import {
  PageContainer,
  SectionCard,
  Tabs,
  Modal,
  Button,
} from 'components/ds';

import ProfileForm from 'components/Users/ProfileForm';
import AddressCard from 'components/Users/AddressCard';
import AddressForm from 'components/Users/AddressForm';
import UserDeleteForm from 'components/Users/UserDeleteForm';
import FormChangePassword from 'components/Users/FormChangePassword';
import Main2fa from 'components/Users/Main2fa';
import CredentialsForm from 'components/Users/CredentialsForm';
import PasskeysForm from 'components/Users/PasskeysForm';
import PushNotifications from 'components/Notifications/PushNotifications';
// #ux-redesign: mismas secciones legales que el login (Datenschutz · Meine
// Daten · AGB/Terms · Impressum).
import { buildLegalSections } from 'components/Auth/legalSections';
import languages from 'configs/lang';

import { actionGetProfile } from 'store/actions/profilesActions';
import {
  actionUpdateLang,
  actionUpdateDateFormat,
} from 'store/actions/usersActions';

const TAB_KEYS = ['profile', 'security', 'preferences', 'legal'];
const PROFILE_FORM_ID = 'account-profile-form';

const getTabFromUrl = (search) => {
  const params = new URLSearchParams(search);
  const tab = params.get('tab');
  return TAB_KEYS.includes(tab) ? tab : null;
};

const Account = () => {
  const t = useTranslate('account');
  const tLogin = useTranslate('login-page');
  const tAddress = useTranslate('profile-address');
  const tForm = useTranslate('profile-form');
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const isMount = useRef(true);

  const user = useSelector((state) => state.userReducer);
  const { profile } = useSelector((state) => state.profilesReducer);
  const { language, dateFormat, password_expired } = user;
  const user_id = user._id || null;

  const urlTab = getTabFromUrl(location.search);
  const defaultTab = password_expired ? 'security' : 'profile';
  const activeTab = password_expired ? 'security' : urlTab || defaultTab;
  const [nextTab, setNextTab] = useState('0');

  const [addressModalState, setAddressModalState] = useState({
    open: false,
    data: {},
  });

  useEffect(() => {
    if (isMount.current && user_id) {
      dispatch(actionGetProfile());
    }
    return () => {
      isMount.current = false;
    };
  }, [dispatch, user_id]);

  useEffect(() => {
    if (profile && Object.keys(profile).length && nextTab === '2' && !profile.is_ready && !user.password_expired) {
      const initial = profile.address && profile.address.length
        ? profile.address[0]
        : {};
      setAddressModalState({ open: true, data: initial });
    }
  }, [profile]);

  const setTab = (key) => {
    if (password_expired && key !== 'security') return;
    const params = new URLSearchParams(location.search);
    params.set('tab', key);
    history.replace({ pathname: location.pathname, search: `?${params.toString()}` });
  };

  const handleLang = (e) => dispatch(actionUpdateLang(e.target.value));
  const handleDateFormat = (e) => dispatch(actionUpdateDateFormat(e.target.value));

  // Adapter para AddressCard/AddressForm legacy.
  const setAddressState = ({ data, showForm }) => {
    setAddressModalState({ open: !!showForm, data: data || {} });
  };
  const closeAddressModal = () => {
    setAddressModalState({ open: false, data: {} });
  };

  const addresses = Array.isArray(profile?.address) ? profile.address : [];

  // ── Profile panel ──────────────────────────────────────────────────────
  // UN SectionCard con TODO adentro. Botones Save/Cancel al final, atados al
  // form interno con `form={PROFILE_FORM_ID}` (HTML5).
  const profilePanel = (
    <SectionCard title={t('page-title')}>
      <div className="ds-account-profile">
        {/* Datos personales + seguro (form sin botones internos) */}
        <ProfileForm
          profile={profile}
          user={user}
          history={history}
          toggle={(tab) => {
            setNextTab(tab)
          }}
          hideActions
          formId={PROFILE_FORM_ID}
        />

        {/* Direcciones — sub-sección visual, mismo card */}
        {profile.is_ready ? (
          <div className="ds-account-profile__section">
            <div className="ds-account-profile__section-head">
              <h3 className="ds-account-profile__section-title">
                {tAddress('addresses') || tAddress('billing-address')}
              </h3>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setAddressModalState({ open: true, data: {} })}
                iconLeft={<i className="fas fa-plus" />}
              >
                {tAddress('add-new-address')}
              </Button>
            </div>

            {addresses.length === 0 ? (
              <p className="ds-account-profile__empty">
                {tAddress('no-addresses-yet') || tAddress('add-new-address')}
              </p>
            ) : (
              <div className="ds-address-grid">
                {addresses.map((ad, i) => (
                  <AddressCard
                    key={ad._id || i}
                    address={ad}
                    setAddressState={setAddressState}
                  />
                ))}
              </div>
            )}
          </div>
        ) : <></>}
        

        {/* Footer del card — botones de submit del form principal */}
        <div className="ds-account-profile__footer">
          <span className="ds-account-profile__hint">
            * {tForm('mandatory-fields')}
          </span>
          <div className="ds-account-profile__footer-actions">
            {profile.is_ready ? (
              <Button
                variant="secondary"
                size="md"
                onClick={() => history.goBack && history.goBack()}
              >
                {tForm('cancel')}
              </Button>
            ) : <></>}

            <Button
              variant="primary"
              size="md"
              type="submit"
              form={PROFILE_FORM_ID}
            >
              {tForm('save')}
            </Button>
          </div>
        </div>
      </div>
    </SectionCard>
  );

  // ── Security panel ─────────────────────────────────────────────────────
  const securityPanel = (
    <div className="ds-account-stack">
      <SectionCard>
        <FormChangePassword view={profile.is_ready ? 'settings' : 'onboarding'} />
      </SectionCard>
      {profile.is_ready && !password_expired && (
        <SectionCard>
          <Main2fa />
        </SectionCard>
      )}
      {profile.is_ready && !password_expired && (
        <SectionCard>
          <CredentialsForm />
        </SectionCard>
      )}
      {profile.is_ready && !password_expired && (
        <SectionCard>
          <PasskeysForm />
        </SectionCard>
      )}

      {/* Delete account */}
      {(profile.is_ready && !user.password_expired) && (
        <SectionCard>
          <UserDeleteForm />
        </SectionCard>
      )}
    </div>
  );

  // ── Preferences panel ──────────────────────────────────────────────────
  const preferencesPanel = (
    <div className="ds-account-stack">
      <SectionCard title={t('preferences-display')}>
        <div className="ds-account-prefs-grid">
          <FormGroup>
            <label>{t('preferences-date-format')}</label>
            <Input
              type="select"
              name="dateFormat"
              defaultValue={dateFormat}
              onChange={handleDateFormat}
            >
              <option value="DD.MM.YYYY">
                DD.MM.YYYY ({moment().format('DD.MM.YYYY')})
              </option>
              <option value="MM-DD-YYYY">
                MM-DD-YYYY ({moment().format('MM-DD-YYYY')})
              </option>
              <option value="DD-MM-YYYY">
                DD-MM-YYYY ({moment().format('DD-MM-YYYY')})
              </option>
              <option value="YYYY-MM-DD">
                YYYY-MM-DD ({moment().format('YYYY-MM-DD')})
              </option>
            </Input>
          </FormGroup>
          <FormGroup>
            <label>{t('preferences-language')}</label>
            <Input
              type="select"
              name="language"
              defaultValue={language}
              onChange={handleLang}
            >
              {Object.keys(languages).map((val) => (
                <option key={val} value={val}>
                  {languages[val].name}
                </option>
              ))}
            </Input>
          </FormGroup>
        </div>
      </SectionCard>
      <SectionCard>
        <PushNotifications />
      </SectionCard>
    </div>
  );

  // ── Legal panel ──────────────────────────────────────────────────────────
  // Mismas secciones legales que el login (Datenschutz · Meine Daten · AGB /
  // Terms of Service · Impressum). Reusa buildLegalSections de components/Auth.
  const legalSections = useMemo(
    () => buildLegalSections(language, {
      datenschutz: tLogin('legal-datenschutz'),
      meineDaten:  tLogin('legal-meine-daten'),
      agb:         tLogin('legal-agb'),
      impressum:   tLogin('legal-impressum'),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [language]
  );

  const legalPanel = (
    <div className="ds-account-stack">
      {legalSections.map((s) => (
        <SectionCard key={s.id} title={s.label}>
          {s.content}
        </SectionCard>
      ))}
    </div>
  );

  const tabs = useMemo(
    () => [
      {
        key: 'profile',
        label: t('tab-profile'),
        icon: <i className="icon-user-active" />,
        content: profilePanel,
        disabled: password_expired,
      },
      {
        key: 'security',
        label: t('tab-security'),
        icon: <i className="icon-shield1" />,
        content: securityPanel,
      },
      {
        key: 'preferences',
        label: t('tab-preferences'),
        icon: <i className="icon-settings1" />,
        content: preferencesPanel,
        disabled: password_expired || !profile.is_ready,
      },
      {
        key: 'legal',
        label: t('tab-legal'),
        icon: <i className="icon-note" />,
        content: legalPanel,
        disabled: password_expired || !profile.is_ready,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, password_expired, language, dateFormat, profile, addressModalState]
  );

  const editingAddress = addressModalState.data && addressModalState.data._id;
  const addressModalTitle = editingAddress
    ? tAddress('edit-address') || tAddress('save')
    : tAddress('add-new-address');

  return (
    <PageContainer
      title={t('page-title')}
      icon="user-active"
      subtitle={password_expired ? t('password-expired-notice') : undefined}
    >
      <Tabs
        orientation="vertical"
        value={activeTab}
        onChange={setTab}
        tabs={tabs}
        ariaLabel={t('page-title')}
      />

      <Modal
        isOpen={addressModalState.open}
        onClose={closeAddressModal}
        title={addressModalTitle}
        size="lg"
      >
        <AddressForm
          addressState={{
            data: addressModalState.data || {},
            showForm: true,
          }}
          setAddressState={setAddressState}
        />
      </Modal>
    </PageContainer>
  );
};

export default Account;
// #ux-redesign-end
